import { runDiscoveryRead } from "./discoveryReadQueue.js";
// REST client for alert_triage.v1.AlertTriageService: finding investigations
// and the analysts who can own them.
//
// An investigation is addressed by its incident id, which rides the path as
// protobuf bytes (base64). The uuid conversion lives in reviewClient.js, the
// sanctioned boundary, and is borrowed here.
import { idForPath, restGet, restPost } from "./restClient.js";
import { bytesBase64ToUuid, enumSuffix, parseTimestampMs, pick, uuidToBytesBase64 } from "./reviewClient.js";


function bytes(uuid, field) {
    const b64 = uuidToBytesBase64(uuid);
    if (!b64) throw new Error(`${field} is not a uuid`);
    return b64;
}
function verdictToWire(verdict) {
    return verdict ? `ALERT_VERDICT_${String(verdict).toUpperCase()}` : "ALERT_VERDICT_UNSPECIFIED";
}

function incidentStatusToWire(status) {
    return status ? `INCIDENT_STATUS_${String(status).toUpperCase()}` : "INCIDENT_STATUS_UNSPECIFIED";
}

function adaptIncident(wire) {
    return {
        incidentId: bytesBase64ToUuid(pick(wire, "incident_id", "incidentId")),
        title: pick(wire, "title", "title") ?? "",
        summary: pick(wire, "summary", "summary") ?? "",
        status: enumSuffix("INCIDENT_STATUS_", pick(wire, "status", "status")) || "open",
        ownerUserId: bytesBase64ToUuid(pick(wire, "owner_user_id", "ownerUserId")),
        createdByUserId: bytesBase64ToUuid(pick(wire, "created_by_user_id", "createdByUserId")),
        createdAtMs: parseTimestampMs(pick(wire, "created_at", "createdAt")),
        updatedAtMs: parseTimestampMs(pick(wire, "updated_at", "updatedAt")),
        closedAtMs: parseTimestampMs(pick(wire, "closed_at", "closedAt")),
    };
}

// ── Assignees ──────────────────────────────────────────────────────────

export async function listAssignableUsers() {
    const json = await restGet("/v1/alert-assignees");
    return (pick(json, "users", "users") ?? []).map((u) => ({
        userId: bytesBase64ToUuid(pick(u, "user_id", "userId")),
        email: pick(u, "email", "email") ?? "",
    }));
}

// Explicit finding collections reuse incident metadata, with their own membership and decisions.
export function adaptFindingInvestigation(wire) {
    const count = (snake, camel) => Number(pick(wire, snake, camel) ?? 0);
    return {
        ...adaptIncident(wire.incident ?? {}), version: String(wire.version ?? "0"),
        verdict: enumSuffix("ALERT_VERDICT_", wire.verdict) || null,
        findingCount: count("finding_count", "findingCount"), sessionCount: count("session_count", "sessionCount"), endpointCount: count("endpoint_count", "endpointCount"),
        setAsideCount: count("set_aside_count", "setAsideCount"),
        findings: (wire.findings ?? []).map(adaptInvestigationMember),
        activity: (wire.activity ?? []).map((e) => ({ ...adaptIncident(e.incident ?? {}),
            operationId: bytesBase64ToUuid(pick(e,"operation_id","operationId")), actorUserId: bytesBase64ToUuid(pick(e,"actor_user_id","actorUserId")),
            atMs: parseTimestampMs(pick(e,"created_at","createdAt")), note: e.note ?? "", added: Number(e.added ?? 0), removed: Number(e.removed ?? 0),
            setAside: Number(e.set_aside ?? e.setAside ?? 0), restored: Number(e.restored ?? 0),
            verdict: enumSuffix("ALERT_VERDICT_",e.verdict) || null,
            decisionRecorded: Boolean(pick(e, "decision_recorded", "decisionRecorded")),
        })),
        nextMemberPageToken: pick(wire,"next_member_page_token","nextMemberPageToken") || "",
        nextActivityPageToken: pick(wire,"next_activity_page_token","nextActivityPageToken") || "",
        canDelete: Boolean(pick(wire, "can_delete", "canDelete")),
    };
}
function investigationResponse(wire, id) {
    const result = adaptFindingInvestigation(wire);
    if (result.incidentId !== id || !/^\d+$/.test(result.version) || [result.findingCount,result.sessionCount,result.endpointCount].some((n) => !Number.isSafeInteger(n) || n < 0 || n > 5000)
        || result.findings.some((f) => !f.finding_id || !f.report_id || !f.trace_id)) throw new Error("The investigation response did not match the saved collection.");
    return result;
}
const investigationPath = (id) => `/v1/finding-investigations/${idForPath(bytes(id,"incidentId"))}`;
export async function getFindingInvestigation(id, { memberPageToken = "", activityPageToken = "", signal } = {}) {
    return investigationResponse(await restGet(investigationPath(id), { member_page_token: memberPageToken, activity_page_token: activityPageToken }, { signal }), id);
}
export async function listFindingInvestigations({ pageToken = "", status = "", signal } = {}) {
    const wire = await runDiscoveryRead(() => restGet("/v1/finding-investigations", { page_token: pageToken, ...(status ? { status: incidentStatusToWire(status) } : {}) }, { signal }), { signal });
    return { investigations: (wire.investigations ?? []).map(adaptFindingInvestigation), nextPageToken: pick(wire,"next_page_token","nextPageToken") || "" };
}
export async function saveFindingInvestigation(id, change) {
    const body = { operationId: bytes(change.operationId,"operationId"), expectedVersion: String(change.expectedVersion),
        addFindingIds: (change.addFindingIds ?? []).map((id) => bytes(id,"findingId")), removeFindingIds: (change.removeFindingIds ?? []).map((id) => bytes(id,"findingId")),
        ...(change.undoOperationId ? { undoOperationId: bytes(change.undoOperationId, "undoOperationId") } : {}),
        ...(change.setAsideFindingIds?.length ? { setAsideFindingIds: change.setAsideFindingIds.map((id) => bytes(id, "findingId")), setAsideReason: `INVESTIGATION_SET_ASIDE_REASON_${String(change.setAsideReason).toUpperCase()}` } : {}),
        ...(change.restoreFindingIds?.length ? { restoreFindingIds: change.restoreFindingIds.map((id) => bytes(id, "findingId")) } : {}),
        sourceJson: JSON.stringify(change.source ?? {}) };
    if (change.timeRange) { body.periodStart = change.timeRange.absolute.start; body.periodEnd = change.timeRange.absolute.end; }
    for (const field of ["title","summary","note"]) if (change[field] != null) body[field] = change[field];
    if (change.status) body.status = incidentStatusToWire(change.status);
    if (Object.hasOwn(change,"verdict")) body.verdict = verdictToWire(change.verdict);
    if (Object.hasOwn(change,"ownerUserId")) {
        if (change.ownerUserId) body.ownerUserId = bytes(change.ownerUserId,"ownerUserId"); else body.clearOwner = true;
    }
    return investigationResponse(await restPost(`${investigationPath(id)}:save`, body), id);
}

export async function deleteFindingInvestigation(id, expectedVersion) {
    await restPost(`${investigationPath(id)}:delete`, { expectedVersion: String(expectedVersion) });
}

function adaptInvestigationMember(f) { return {
            finding_id: bytesBase64ToUuid(pick(f,"finding_id","findingId")), report_id: bytesBase64ToUuid(pick(f,"report_id","reportId")),
            trace_id: bytesBase64ToUuid(pick(f,"trace_id","traceId")), endpoint_id: bytesBase64ToUuid(pick(f,"endpoint_id","endpointId")),
            completed_at: new Date(parseTimestampMs(pick(f,"completed_at","completedAt"))).toISOString(),
            addedAtMs: parseTimestampMs(pick(f,"added_at","addedAt")), addedByUserId: bytesBase64ToUuid(pick(f,"added_by_user_id","addedByUserId")),
            source: JSON.parse(pick(f,"source_json","sourceJson") || "{}"),
            sourceOperationId: bytesBase64ToUuid(pick(f,"source_operation_id","sourceOperationId")),
            setAsideReason: (enumSuffix("INVESTIGATION_SET_ASIDE_REASON_", pick(f,"set_aside_reason","setAsideReason")) || "").toLowerCase() || null,
            setAsideAtMs: parseTimestampMs(pick(f,"set_aside_at","setAsideAt")),
}; }


export async function browseFindingInvestigation(id, { grouping = "session", query = "", subjectId, unattributedEndpoint = false, findingId, subjectPageToken = "", memberPageToken = "", includeSetAside = false, signal } = {}) {
    const wire = await restPost(`${investigationPath(id)}:browse`, {
        grouping: grouping === "endpoint" ? "INVESTIGATION_GROUPING_ENDPOINT" : "INVESTIGATION_GROUPING_SESSION",
        query, subjectPageToken, memberPageToken, unattributedEndpoint, includeSetAside,
        ...(subjectId ? { subjectId: bytes(subjectId, "subjectId") } : {}),
        ...(findingId ? { findingId: bytes(findingId, "findingId") } : {}),
    }, { signal });
    const subject = (s) => s ? {
        id: bytesBase64ToUuid(pick(s, "subject_id", "subjectId")),
        unattributed: Boolean(pick(s, "unattributed_endpoint", "unattributedEndpoint")),
        name: pick(s, "endpoint_name", "endpointName") || "",
        findingCount: Number(pick(s, "finding_count", "findingCount") || 0),
        latestReviewAt: parseTimestampMs(pick(s, "latest_review_at", "latestReviewAt")),
    } : null;
    const result = {
        version: String(wire.version), subjects: (wire.subjects ?? []).map(subject),
        selected: subject(pick(wire, "selected_subject", "selectedSubject")),
        findings: (wire.findings ?? []).map(adaptInvestigationMember),
        nextSubjectPageToken: pick(wire, "next_subject_page_token", "nextSubjectPageToken") || "",
        nextMemberPageToken: pick(wire, "next_member_page_token", "nextMemberPageToken") || "",
        previousSubjectPageToken: pick(wire, "previous_subject_page_token", "previousSubjectPageToken") || "",
        previousMemberPageToken: pick(wire, "previous_member_page_token", "previousMemberPageToken") || "",
        subjectPageToken: pick(wire, "subject_page_token", "subjectPageToken") || "",
        memberPageToken: pick(wire, "member_page_token", "memberPageToken") || "",
    };
    if (!/^\d+$/.test(result.version) || result.subjects.length > 100 || result.findings.length > 50
        || [...result.subjects, ...(result.selected ? [result.selected] : [])].some((s) => (!s.id && !s.unattributed) || (s.id && s.unattributed) || !Number.isInteger(s.findingCount) || s.findingCount < 1 || s.findingCount > 5000)
        || result.findings.some((f) => !f.finding_id || !f.report_id || !f.trace_id || (grouping === "endpoint" ? (f.endpoint_id || null) !== result.selected?.id : f.trace_id !== result.selected?.id))
        || (findingId && !result.findings.some((f) => f.finding_id === findingId))) throw new Error("The saved findings did not match the selected subject.");
    return result;
}

export async function summarizeFindingInvestigations({ status = "open", signal } = {}) {
    const wire = await runDiscoveryRead(() => restPost("/v1/finding-investigations:summarize", { status: incidentStatusToWire(status) }, { signal }), { signal });
    const totals = [pick(wire, "investigation_count", "investigationCount"), pick(wire, "unassigned_count", "unassignedCount"), pick(wire, "finding_count", "findingCount")];
    // The gateway emits unpopulated fields, so a missing total is a changed
    // response contract, not a tenant with nothing saved. Coalescing it to 0
    // would render a confident wrong number.
    if (totals.some((value) => value == null)) throw new Error("Investigation totals are missing a field.");
    const values = totals.map(Number);
    if (values.some((v) => !Number.isSafeInteger(v) || v < 0)) throw new Error("Invalid investigation totals.");
    return { investigations: values[0], unassigned: values[1], findings: values[2] };
}
